// Where an offer sends people.
//
// Three columns answer it — `cta_id` (a meeting link from Run), `page_id` (one
// of her pages in Sites) and `link_url` (anything hosted elsewhere) — and
// exactly one of them is ever set. supabase/0018 has the reasoning.
//
// EVERY READER GOES THROUGH `destinationOf`. The ladder's column, the offer
// form's field and the Meeting Links "used in" count all ask the same question,
// and three readers checking the three columns in three different orders is how
// an offer ends up naming a meeting link in one place and a page in another.
// `pointAt` is the one way to WRITE it, and it always clears the other two.

import { ctaLabel, ctaStatus } from "./useCtas";
import { sequenceTypeDef } from "./outreach";
import { scriptLabel, scriptTypeDef } from "./callScripts";
import { presentationLabel, presentationTypeDef } from "./presentations";

export const DESTINATION_KINDS = ["page", "cta", "url"];

/** The patch that points an offer at one thing and nothing else. */
export function pointAt(kind, value) {
  return {
    pageId: kind === "page" ? value || null : null,
    ctaId: kind === "cta" ? value || null : null,
    linkUrl: kind === "url" ? (String(value || "").trim() || null) : null,
  };
}

/** Clears all three. */
export const pointNowhere = () => pointAt(null);

/** How a page reads as an address: `tevrawood.com/assessment`, or `/assessment`
 *  when its site has no domain yet. A site's front page (`home`) is the domain
 *  on its own, because that is what anyone would type. */
export function pageAddress(page, site) {
  const slug = String(page?.slug || "").trim();
  const domain = String(site?.domain || "").trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
  if (domain) return slug && slug !== "home" ? `${domain}/${slug}` : domain;
  return `/${slug}`;
}

/**
 * What an offer points at, resolved against what she has.
 *
 * @returns null when it points nowhere, otherwise
 *   { kind, id, label, detail, broken }
 *   `broken` — it names something that is not there any more (deleted in
 *   another tab: the database has nulled the column, our copy has not caught
 *   up), or a meeting link with no URL. Callers show it, never hide it: "Sends
 *   people nowhere" and "sends people to a page you deleted" look identical
 *   otherwise, and only one of them is something she chose.
 */
export function destinationOf(offer, { pages = [], sites = [], ctas = [] } = {}) {
  if (!offer) return null;
  if (offer.pageId) {
    const page = (pages || []).find((p) => p.id === offer.pageId);
    if (!page) return { kind: "page", id: offer.pageId, label: "A page that has been deleted", detail: "", broken: true };
    /* A page with no site is the first site's, as on the Sites screen. */
    const site = (sites || []).find((s) => s.id === (page.siteId || sites?.[0]?.id)) || null;
    const title = String(page.title || "").trim() || "Untitled page";
    const siteName = site ? String(site.name || "").trim() || "Untitled site" : "";
    return {
      kind: "page",
      id: page.id,
      /* SITE › PAGE, never the page alone: every main site has a Services
         Page and every landing site a Thank-you Page (her note, 2026-09-16). */
      label: siteName ? `${siteName} › ${title}` : title,
      detail: pageAddress(page, site),
      site: siteName,
      broken: false,
    };
  }
  if (offer.ctaId) {
    const cta = (ctas || []).find((c) => c.id === offer.ctaId);
    if (!cta) return { kind: "cta", id: offer.ctaId, label: "A meeting link that has been deleted", detail: "", broken: true };
    const noUrl = ctaStatus(cta) === "no_url";
    return {
      kind: "cta",
      id: cta.id,
      label: ctaLabel(cta),
      detail: noUrl ? "No URL yet" : cta.link,
      buttonText: String(cta.buttonText || "").trim(),
      broken: noUrl,
    };
  }
  const url = String(offer.linkUrl || "").trim();
  if (url) return { kind: "url", id: null, label: url, detail: "", broken: false };
  return null;
}

/** The landing site already made for this offer, if there is one — so "Make a
 *  landing page for this offer" twice fills one site rather than making two. */
export function landingSiteFor(offer, sites = []) {
  if (!offer?.id) return null;
  return (sites || []).find((s) => s.kind === "landing" && s.offerId === offer.id) || null;
}

/** Offers that point at any of these pages — the ones whose copy goes stale
 *  when a page, or a whole site, is deleted. */
export function offersPointingAt(offers = [], pageIds = []) {
  const ids = new Set(pageIds || []);
  return (offers || []).filter((o) => o?.pageId && ids.has(o.pageId));
}

/**
 * Clear a destination id that no longer resolves, before it reaches a foreign
 * key — a page or link deleted in another tab would otherwise fail the whole
 * save and lose everything else she typed.
 *
 * `justMade` is the id of a page or link created a moment ago, which the
 * caller's lists cannot contain yet: React state from before the create is
 * what the save reads. Without it, "New meeting link" and "Make a landing page
 * for this offer" both created their record and then saved the offer pointing
 * NOWHERE, with no error — found by clicking, 2026-09-16.
 */
export function dropDeadIds(offer, { ctas = [], pages = [], justMade = null } = {}) {
  const o = { ...offer };
  if (o.ctaId && o.ctaId !== justMade && !(ctas || []).some((c) => c.id === o.ctaId)) o.ctaId = null;
  if (o.pageId && o.pageId !== justMade && !(pages || []).some((p) => p.id === o.pageId)) o.pageId = null;
  return o;
}

/**
 * What is built FOR this offer — the offer artboard's "Supported by", the
 * reverse of every `offer_id` that points here.
 *
 * It replaced the free-text "Tied to" field (her call, 2026-09-16: "the field
 * can go away and it can just show it if it is tied to something"). Nothing
 * here is typed; a record appears because it names this offer.
 *
 * Read: a site and a course (behind their cogs), a page (behind its address
 * cog), an outreach sequence (its settings), a call script (its settings, added
 * 2026-09-16) and a saved social post (its "Supports which offer"). Talks have the column but no tool yet.
 *
 * @returns [{ key, label, items: [{ id, name, meta, record }] }], empty groups left out
 */
/* How a platform is written on screen — "LinkedIn", not "Linkedin". */
const PLATFORM_NAME = { linkedin: "LinkedIn", instagram: "Instagram", facebook: "Facebook", email: "Email" };
const platformName = (id) => PLATFORM_NAME[id] || (id ? id.charAt(0).toUpperCase() + id.slice(1) : "");

export function supportersOf(offer, { sites = [], pages = [], courses = [], sequences = [], scripts = [], presentations = [], posts = [], pagesOf, modulesOf, sequenceName } = {}) {
  if (!offer?.id) return [];
  const mine = (list) => (list || []).filter((r) => r?.offerId === offer.id);
  const count = (n, one) => `${n} ${one}${n === 1 ? "" : "s"}`;
  const groups = [
    { key: "sites", label: "Sites", items: mine(sites).map((x) => ({
      id: x.id, record: x,
      name: String(x.name || "").trim() || "Untitled site",
      meta: [x.kind === "landing" ? "Landing site" : "Main site", pagesOf ? count(pagesOf(x.id).length, "page") : null].filter(Boolean).join(" · "),
    })) },
    /* A page names its site, because every main site has a Services Page. */
    { key: "pages", label: "Pages", items: mine(pages).map((p) => {
      const site = (sites || []).find((x) => x.id === (p.siteId || sites?.[0]?.id));
      const title = String(p.title || "").trim() || "Untitled page";
      return { id: p.id, record: p, name: site ? `${String(site.name || "").trim() || "Untitled site"} › ${title}` : title, meta: `/${p.slug || ""}` };
    }) },
    { key: "courses", label: "Courses", items: mine(courses).map((c) => ({
      id: c.id, record: c,
      name: String(c.title || c.name || "").trim() || "Untitled course",
      meta: modulesOf ? count(modulesOf(c.id).length, "module") : "",
    })) },
    { key: "sequences", label: "Outreach sequences", items: mine(sequences).map((q) => ({
      id: q.id, record: q,
      name: sequenceName ? sequenceName(q) : String(q.name || "").trim() || "Untitled sequence",
      // What the sequence is for — a sequence has no single platform any more.
      meta: sequenceTypeDef(q.type)?.label || "",
    })) },
    { key: "scripts", label: "Call scripts", items: mine(scripts).map((c) => ({
      id: c.id, record: c,
      name: scriptLabel(c),
      meta: [scriptTypeDef(c.type)?.label, c.duration].filter(Boolean).join(" · "),
    })) },
    { key: "presentations", label: "Presentations", items: mine(presentations).map((d) => ({
      id: d.id, record: d, name: presentationLabel(d), meta: presentationTypeDef(d.type)?.label || "",
    })) },
    { key: "posts", label: "Social posts", items: mine(posts).map((x) => ({
      id: x.id, record: x,
      name: String(x.title || "").trim() || String(x.caption || "").trim().slice(0, 60) || "Untitled post",
      meta: platformName(x.platform),
    })) },
  ];
  return groups.filter((g) => g.items.length);
}
